/**
 * How each type of extractor is drawn, by its type in the rules and level by level: the parts that
 * stand still, the core that fills as it stores, the part that turns while it draws, and the core's
 * height when full. A level without a look of its own wears the highest one below it, so an
 * upgrade that should look different adds a look here; one that only changes numbers adds nothing.
 * The placed props, the placing preview and the planet's glow all draw from this table; a new type
 * is a row here (and a colour in core/palette.ts).
 */
import { CHARGE, HEAVY_CHARGE, type ChargeParts } from './charge';
import { INFUSER_ARM, INFUSER_FRAME, INFUSER_PILE, PILE_FULL } from './infuser';
import type { MachinePart } from './part';
import { CORE_FULL, RESONATOR_CORE, RESONATOR_FRAME, RESONATOR_RING } from './resonator';
import { STACK_FULL, STONECUTTER_FRAME, STONECUTTER_SAW, STONECUTTER_STACK } from './stonecutter';

export interface MachineLook {
  frame: readonly MachinePart[];
  /** A unit tall from its foot: scaled to how full it is, up to `coreFull`. */
  core: MachinePart;
  coreFull: number;
  turning: MachinePart;
}

/** A charge as a machine: its heart fills as the fuse burns, its splinters turn while it hums. */
const chargeLook = (c: ChargeParts): MachineLook => ({
  frame: c.frame,
  core: c.heart,
  coreFull: c.heartFull,
  turning: c.splinters,
});

/** Each type's looks, level 1 first. */
const LOOKS: Readonly<Record<string, readonly MachineLook[]>> = {
  crystal: [
    {
      frame: RESONATOR_FRAME,
      core: RESONATOR_CORE,
      coreFull: CORE_FULL,
      turning: RESONATOR_RING,
    },
  ],
  stone: [
    {
      frame: STONECUTTER_FRAME,
      core: STONECUTTER_STACK,
      coreFull: STACK_FULL,
      turning: STONECUTTER_SAW,
    },
  ],
  // the crystal charges (plan 34)
  charge: [chargeLook(CHARGE)],
  'heavy-charge': [chargeLook(HEAVY_CHARGE)],
  // the infuser (plan 36): its basket fills as it is loaded, its spreader turns while it lays
  infuser: [
    { frame: INFUSER_FRAME, core: INFUSER_PILE, coreFull: PILE_FULL, turning: INFUSER_ARM },
  ],
};

/** How an extractor of `type` at `level` looks: its level's look, or the highest one below it. */
export function lookOf(type: string, level: number): MachineLook | undefined {
  const looks = LOOKS[type] ?? [];
  return looks[Math.max(0, Math.min(level, looks.length) - 1)];
}

/** The types in the table's order: the planet's glow colours are numbered after it, from 1. */
export const GLOW_TYPES: readonly string[] = Object.keys(LOOKS);

/** The glow number of ground drawn by an extractor of `type` (PlanetView.drawn); 0 for none. */
export const glowOf = (type: string): number => GLOW_TYPES.indexOf(type) + 1;

/** Every part of every type and level, once each, for the props to make their meshes. */
export const ALL_MACHINE_PARTS: readonly MachinePart[] = [
  ...new Map(
    Object.values(LOOKS)
      .flat()
      .flatMap((look) => [...look.frame, look.core, look.turning])
      .map((part) => [part.name, part] as const),
  ).values(),
];
